import { Col, Row } from "antd";
import { useTranslation } from "next-i18next";
import { useRouter } from "next/router";
import { color } from "src/styles/globals";
import { AllCoverageItemType } from "src/types/plan";
import { CoverageType } from "src/types/productCategory";
import { getCoverageValueByType } from "src/utils/coverageItem";
import styled from "styled-components";
import {
  Panel,
  Title,
  Body,
  Collapse as SundayCollapse,
} from "sunday-component";

const Collapse = styled(SundayCollapse)`
  padding-left: 24px;
  .sunday-component-collapse-header,
  .sunday-component-collapse-header-collapsible-only {
    border: 0px !important;
    padding: 0px !important;
  }
  .sunday-component-collapse-header-text {
    width: fit-content;
    padding-right: 24px;
  }
`;

const GroupCollapse = styled(SundayCollapse)`
  width: 100%;
  .sunday-component-collapse-header,
  .sunday-component-collapse-header-collapsible-only {
    border: 0px !important;
    padding: 0px !important;
    margin-bottom: 0;
  }
  .sunday-component-collapse-header-text {
    width: 100%;
  }
  .sunday-component-collapse-header.sunday-component-collapse-header-collapsible-only {
    justify-content: space-between;
    flex-direction: row-reverse;
    align-items: center;
  }
  .sunday-component-collapse-extra {
    display: none;
  }
`;

const Container = styled(Row)`
  width: 100%;
`;

const AdditionalContainer = styled(Row)`
  width: 100%;
  padding: 16px 12px 16px 12px;
  font-size: 21px;
  color: ${color.charcoal10};
  p,
  ul {
    margin-bottom: 0px;
  }
  li {
    line-height: 24px;
    margin-bottom: 8px;
  }
`;

export type CoverageItemTableProps = {
  allCoverageItems: AllCoverageItemType[];
  insurerPlanCode: string;
  tablePrimaryColor: string;
  tableSecondaryColor: string;
};

export const CoverageItemTable = ({
  allCoverageItems,
  insurerPlanCode,
  tablePrimaryColor,
  tableSecondaryColor,
}: CoverageItemTableProps): JSX.Element => {
  const { locale } = useRouter();
  const { t } = useTranslation("plan");

  const mapCoverageItem = (
    coverage: AllCoverageItemType,
    coverageType: string,
    index: number
  ) => (
    <Col
      key={coverage?.coverageItem.code}
      span={24}
      style={{
        background: index % 2 === 0 ? tablePrimaryColor : tableSecondaryColor,
      }}
    >
      <Row gutter={1}>
        <Col
          span={12}
          style={{
            borderRight: "1px solid #FFFFFF",
            padding: "16px 11px 4px",
          }}
        >
          {coverage?.coverageItem.detail ? (
            <Collapse>
              <Panel
                key={coverage?.coverageItem.code}
                header={coverage?.coverageItem.displayName[locale]}
              >
                {coverage?.coverageItem.detail[locale]}
              </Panel>
            </Collapse>
          ) : (
            <Title
              level={7}
              data-cy={`plan-coverage-detail.${insurerPlanCode}.${coverageType}.coverage.${
                index + 1
              }.display-name`}
            >
              {coverage?.coverageItem.displayName[locale]}
            </Title>
          )}
        </Col>
        <Col
          span={12}
          style={{
            borderLeft: "1px solid #FFFFFF",
            padding: "16px 0px",
            alignSelf: "center",
          }}
        >
          <Body
            data-cy={`plan-coverage-detail.${insurerPlanCode}.${coverageType}.coverage.${
              index + 1
            }.value`}
            level={7}
            style={{ textAlign: "center" }}
          >
            {getCoverageValueByType(
              coverage?.coverageItem.valueType,
              coverage.value,
              locale
            )}
          </Body>
        </Col>
      </Row>
    </Col>
  );

  const mapGroupCoverages = (
    coverages: AllCoverageItemType[],
    coverageType: string
  ) => (
    <GroupCollapse defaultActiveKey={coverages[0].coverageItem.type}>
      <Panel
        style={{ width: "100%" }}
        key={coverages[0].coverageItem.type}
        header={
          <Row>
            <Col span={24}>
              <Title
                level={5}
                style={{ textAlign: "center", margin: "16px 0px" }}
              >
                {t(
                  `planCoverageDetail.groupHeader.${coverages[0].coverageItem.type}`
                )}
              </Title>
            </Col>
          </Row>
        }
      >
        {coverages.map((coverage, groupIndex) =>
          mapCoverageItem(coverage, coverageType, groupIndex)
        )}
      </Panel>
    </GroupCollapse>
  );

  const maincoverages = allCoverageItems.filter(
    (item) =>
      item.coverageItem.type !== CoverageType.other &&
      item.coverageItem.type !== CoverageType.additional
  );
  const otherCoverages = allCoverageItems.filter(
    (item) => item.coverageItem.type === CoverageType.other
  );
  const additionalDetails = allCoverageItems.filter(
    (item) => item.coverageItem.type === CoverageType.additional
  );

  const groupedCoverages = maincoverages.reduce(
    (
      prevCoverages: (AllCoverageItemType | AllCoverageItemType[])[],
      currentCoverage
    ) => {
      // case main coverage (no group)
      if (currentCoverage.coverageItem.type === CoverageType.main) {
        return [...prevCoverages, currentCoverage];
      }
      // case type same as previous group
      if (
        Array.isArray(prevCoverages[prevCoverages.length - 1]) &&
        prevCoverages[prevCoverages.length - 1][0].coverageItem.type ===
          currentCoverage.coverageItem.type
      ) {
        return [
          ...prevCoverages.slice(0, prevCoverages.length - 1),
          [
            ...(prevCoverages[
              prevCoverages.length - 1
            ] as AllCoverageItemType[]),
            currentCoverage,
          ],
        ];
      }
      // case new group
      return [...prevCoverages, [currentCoverage]];
    },
    []
  );

  return (
    <Container justify="center">
      {groupedCoverages.map((groupedCoverage, index) =>
        Array.isArray(groupedCoverage)
          ? mapGroupCoverages(groupedCoverage, "main-benefit")
          : mapCoverageItem(groupedCoverage, "main-benefit", index)
      )}
      {otherCoverages.length > 0 &&
        mapGroupCoverages(otherCoverages, "other-benefit")}
      {additionalDetails.length > 0 && (
        <GroupCollapse defaultActiveKey={[CoverageType.additional]}>
          <Panel
            style={{ width: "100%" }}
            key={CoverageType.additional}
            header={
              <Row>
                <Col span={24}>
                  <Title
                    level={5}
                    style={{ textAlign: "center", margin: "16px 0px" }}
                  >
                    {t("planCoverageDetail.groupHeader.additional")}
                  </Title>
                </Col>
              </Row>
            }
          >
            <AdditionalContainer
              style={{ background: tablePrimaryColor, width: "100%" }}
            >
              {additionalDetails.map(({ value }) => {
                return (
                  <Row
                    dangerouslySetInnerHTML={{
                      __html: value[locale],
                    }}
                    style={{ width: "100%" }}
                  />
                );
              })}
            </AdditionalContainer>
          </Panel>
        </GroupCollapse>
      )}
    </Container>
  );
};
